import React, { useContext, useEffect, useState } from 'react';
import AppContext from 'app/AppContext';
import { HistoricalPercentageChangeConfig } from 'widgets/historicalPercentageChange/store/HistoricalPercentageChangeConfig';
import Legend from 'utils/components/Legend/Legend.tsx';
import { WidgetLegend } from 'widgets/WidgetLegend/WidgetLegend.tsx';
import { HistoricalPercentageChangeDataItem } from 'widgets/historicalPercentageChange/data/historicalPercentageChangeDataItem';
import { useToggleConfigOption } from 'widgets/store/configuration/hooks.ts';
import {
    NumberFormat,
    timeFormatConstants,
} from '@instinet-analytics/core-lib';
import { BarMouseOver } from 'widgets/historicalPercentageChange/components/HistoricalPercentageChangeChart/barMouseOver';
import { DateTime } from 'luxon';

const { date } = timeFormatConstants;

export type LegendData = { price: string; volume: string; date: string };

const priceTooltip = {
    title: 'Price',
    body: ' is the PNC or todays last price',
};

export const volumeTooltip = {
    title: 'Volume',
    body: '',
};

export function HistoricalPercentageChangeWidgetChartLegends({
    defaultItem,
    mouseOver,
}: {
    defaultItem: HistoricalPercentageChangeDataItem | undefined;
    mouseOver: BarMouseOver;
}) {
    const { numberFormat } = useContext(AppContext);

    const defaultLegendData = deriveLegendData(defaultItem, numberFormat);
    const [legendData, setLegendData] = useState<LegendData | undefined>(
        defaultLegendData
    );

    const { value: volumeSeriesHidden, toggle: toggleVolumeSeriesHidden } =
        useToggleConfigOption<HistoricalPercentageChangeConfig>('hideVolume');
    const { value: priceSeriesHidden, toggle: togglePriceSeriesHidden } =
        useToggleConfigOption<HistoricalPercentageChangeConfig>('hidePrice');

    useEffect(() => {
        mouseOver.subscribe(
            'HistoricalPercentageChangeWidgetChartLegends',
            (mouseOverState) => {
                setLegendData(
                    mouseOverState?.item
                        ? deriveLegendData(mouseOverState?.item, numberFormat)
                        : undefined
                );
            }
        );
    });

    const legendItem = legendData ?? defaultLegendData;
    if (!legendItem) {
        return <></>;
    }

    const { price, volume, date: itemDate } = legendItem;
    return (
        <div className="HistoricalPercentageChangeWidgetChartLegends">
            <WidgetLegend<HistoricalPercentageChangeConfig>
                hideLegendPropertyName="hidePriceLegend"
                showSeries={!priceSeriesHidden}
                toggleShowSeries={togglePriceSeriesHidden}
            >
                <Legend
                    className="price-legend"
                    value={price}
                    label="Price"
                    tooltip={priceTooltip}
                    disabled={priceSeriesHidden}
                />
            </WidgetLegend>

            <WidgetLegend<HistoricalPercentageChangeConfig>
                hideLegendPropertyName="hideVolumeLegend"
                showSeries={!volumeSeriesHidden}
                toggleShowSeries={toggleVolumeSeriesHidden}
            >
                <Legend
                    className="volume-legend"
                    value={volume}
                    label="Volume"
                    tooltip={volumeTooltip}
                    disabled={volumeSeriesHidden}
                />
            </WidgetLegend>

            {legendData && (
                <Legend className="date-legend" value={itemDate} label="" />
            )}
        </div>
    );
}

export function deriveLegendData(
    item: HistoricalPercentageChangeDataItem | undefined,
    numberFormat: NumberFormat
): LegendData | undefined {
    if (!item) {
        return undefined;
    }

    return {
        date: DateTime.fromMillis(item.date).toFormat(date),
        price: item.price === undefined ? '' : numberFormat.raw(item.price),
        volume: item.volume === undefined ? '' : numberFormat.n0(item.volume),
    };
}
